/**
 * AuthCallback - OAuth Callback Handler
 * PROJ-025: Azure CIAM & Google OAuth Integration
 *
 * Updated for Frontend-First OAuth Flow (SaaS Architecture)
 *
 * This page handles the redirect after OAuth authentication.
 * In the new flow, OAuth providers redirect directly to frontend with an authorization code.
 * Frontend then exchanges the code with backend for tokens.
 *
 * Flow:
 * 1. OAuth Provider redirects to /auth/callback?code=xxx&state=xxx
 * 2. Frontend extracts code from query string
 * 3. Frontend calls backend /api/v1/auth/{provider}/exchange with code
 * 4. Backend exchanges code for tokens (using client_secret)
 * 5. Backend returns JWT tokens to frontend
 * 6. Frontend stores tokens and completes auth
 *
 * Supports both:
 * - Popup mode: notifies opener window and closes
 * - Redirect mode: stores tokens and navigates to home
 *
 * Legacy support:
 * - Also handles tokens in URL fragment (hash) for backward compatibility
 */

import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react'
import * as auth from '@/lib/auth'
import { isOAuthPopup, notifyOpenerAndClose } from '@/hooks/useOAuthPopup'

type CallbackState = 'processing' | 'exchanging' | 'success' | 'error'

interface CallbackError {
  code: string
  message: string
}

const ERROR_MESSAGES: Record<string, string> = {
  oauth_failed: 'Error al iniciar el proceso de autenticación. Por favor intenta nuevamente.',
  missing_code: 'No se recibió el código de autorización. Por favor intenta nuevamente.',
  invalid_state: 'Error de seguridad: el estado de la sesión no coincide. Por favor intenta nuevamente.',
  callback_failed: 'Error al procesar la autenticación. Por favor intenta nuevamente.',
  access_denied: 'Acceso denegado. No tienes permisos para acceder a esta aplicación.',
  server_error: 'Error del servidor de autenticación. Por favor intenta más tarde.',
  temporarily_unavailable: 'El servicio de autenticación no está disponible temporalmente.',
  exchange_failed: 'Error al intercambiar el código de autorización. Por favor intenta nuevamente.',
  user_not_registered: 'No tienes una cuenta en Lineage Platform. Contacta a tu administrador para recibir una invitación.',
  default: 'Ocurrió un error durante la autenticación. Por favor intenta nuevamente.',
}

/**
 * Exchange authorization code for tokens via backend
 */
async function exchangeCodeForTokens(code: string, provider: string): Promise<{
  access_token: string
  refresh_token: string
  user: {
    id: string
    email: string
    full_name: string
    avatar_url?: string
    role: string
  }
}> {
  const response = await fetch(`/api/v1/auth/${provider}/exchange`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    // Propagate USER_NOT_REGISTERED as a specific error code
    if (errorData.error?.code === 'USER_NOT_REGISTERED') {
      const err = new Error(errorData.error.message) as Error & { code: string }
      err.code = 'USER_NOT_REGISTERED'
      throw err
    }
    throw new Error(errorData.error?.message || `Exchange failed: ${response.status}`)
  }

  const result = await response.json()
  if (!result.success) {
    throw new Error(result.error?.message || 'Exchange failed')
  }

  return result.data
}

/**
 * Detect OAuth provider from state or sessionStorage
 */
function detectProvider(): 'google' | 'azure' | null {
  // Check sessionStorage (set by popup opener)
  const storedProvider = sessionStorage.getItem('oauth_provider')
  if (storedProvider === 'google' || storedProvider === 'azure') {
    return storedProvider
  }

  // Default to google if we can't detect (most common)
  return 'google'
}

export default function AuthCallback() {
  const navigate = useNavigate()
  const location = useLocation()
  const [state, setState] = useState<CallbackState>('processing')
  const [error, setError] = useState<CallbackError | null>(null)
  const [userEmail, setUserEmail] = useState<string | null>(null)

  useEffect(() => {
    async function processCallback() {
      const isPopup = isOAuthPopup()

      try {
        // Check for error in query string (OAuth errors come as query params)
        const queryParams = new URLSearchParams(location.search)
        const errorCode = queryParams.get('error')

        if (errorCode) {
          const errorDescription = queryParams.get('error_description')
          const errorMessage = errorDescription || ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.default

          if (isPopup) {
            notifyOpenerAndClose('oauth_error', errorMessage)
            return
          }

          setError({
            code: errorCode,
            message: errorMessage,
          })
          setState('error')
          return
        }

        // Check for authorization code in query string (new frontend-first flow)
        const code = queryParams.get('code')

        if (code) {
          // New flow: Exchange code with backend
          setState('exchanging')

          const provider = detectProvider()
          if (!provider) {
            throw new Error('Could not detect OAuth provider')
          }

          // Exchange code for tokens
          const tokenData = await exchangeCodeForTokens(code, provider)

          // Store tokens
          auth.setAccessToken(tokenData.access_token)
          auth.setRefreshToken(tokenData.refresh_token)

          // Set user email for display
          setUserEmail(tokenData.user.email)

          // Clear the query string from URL for security
          window.history.replaceState(null, '', '/auth/callback')

          // Clear provider from sessionStorage
          sessionStorage.removeItem('oauth_provider')

          // Success handling
          if (isPopup) {
            notifyOpenerAndClose('oauth_success')
            return
          }

          setState('success')
          setTimeout(() => {
            // Force full page reload to re-initialize auth state in App.tsx
            // navigate() doesn't remount App, so useEffect won't re-run
            window.location.href = '/dashboard'
          }, 1500)
          return
        }

        // Legacy flow: Check for tokens in URL fragment (hash)
        // Backend redirects with: /auth/callback#access_token=xxx&refresh_token=xxx
        const hash = location.hash.substring(1)
        if (hash) {
          const hashParams = new URLSearchParams(hash)
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          const email = hashParams.get('email')

          if (accessToken && refreshToken) {
            // Store tokens
            auth.setAccessToken(accessToken)
            auth.setRefreshToken(refreshToken)

            if (email) {
              setUserEmail(email)
            }

            // Clear the hash from URL for security
            window.history.replaceState(null, '', '/auth/callback')

            // Success handling
            if (isPopup) {
              notifyOpenerAndClose('oauth_success')
              return
            }

            setState('success')
            setTimeout(() => {
              // Force full page reload to re-initialize auth state in App.tsx
              window.location.href = '/dashboard'
            }, 1500)
            return
          }
        }

        // No code or tokens found
        const errorMessage = 'No se recibió información de autenticación. Por favor intenta nuevamente.'

        if (isPopup) {
          notifyOpenerAndClose('oauth_error', errorMessage)
          return
        }

        setError({
          code: 'missing_auth_data',
          message: errorMessage,
        })
        setState('error')
      } catch (err) {
        console.error('Error processing OAuth callback:', err)
        const errWithCode = err as Error & { code?: string }
        const isNotRegistered = errWithCode.code === 'USER_NOT_REGISTERED'
        const errorMessage = isNotRegistered
          ? ERROR_MESSAGES.user_not_registered
          : (err instanceof Error ? err.message : ERROR_MESSAGES.default)

        if (isPopup) {
          notifyOpenerAndClose('oauth_error', errorMessage)
          return
        }

        setError({
          code: isNotRegistered ? 'user_not_registered' : 'processing_error',
          message: errorMessage,
        })
        setState('error')
      }
    }

    processCallback()
  }, [location, navigate])

  const handleRetry = () => {
    // Clear any stored tokens and redirect to login
    auth.clearTokens()
    sessionStorage.removeItem('oauth_provider')
    sessionStorage.removeItem('oauth_popup')
    navigate('/', { replace: true })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-6 p-8">
        {/* Processing State */}
        {state === 'processing' && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Procesando autenticación...
            </h2>
            <p className="text-sm text-muted-foreground">
              Por favor espera mientras verificamos tu identidad.
            </p>
          </div>
        )}

        {/* Exchanging State */}
        {state === 'exchanging' && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Intercambiando credenciales...
            </h2>
            <p className="text-sm text-muted-foreground">
              Estamos completando tu inicio de sesión de forma segura.
            </p>
          </div>
        )}

        {/* Success State */}
        {state === 'success' && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Autenticación exitosa
            </h2>
            {userEmail && (
              <p className="text-sm text-muted-foreground">
                Bienvenido, <span className="font-medium">{userEmail}</span>
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Redirigiendo al inicio...
            </p>
            <div className="flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}

        {/* Error State */}
        {state === 'error' && error && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              {error.code === 'access_denied' || error.code === 'user_not_registered' ? (
                <AlertTriangle className="h-12 w-12 text-yellow-500" />
              ) : (
                <XCircle className="h-12 w-12 text-destructive" />
              )}
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {error.code === 'user_not_registered' ? 'Cuenta no registrada' : 'Error de autenticación'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {error.message}
            </p>
            {error.code && (
              <p className="text-xs text-muted-foreground/60">
                Código: {error.code}
              </p>
            )}
            <button
              onClick={handleRetry}
              className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Volver al inicio
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
